"use client";

import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from "@/components/ui/badge";
import { PersonalInfoForm } from '@/components/PersonalInfoForm';
import MFASettings from '@/components/MFASettings';
import GDPRControls from '@/components/GDPRControls';
import { Skeleton } from '@/components/ui/skeleton';
import { showError } from '@/utils/toast';
import { updateProfile } from '@/lib/auth-utils';

const Profile: React.FC = () => {
  const { user, profile, isLoading, signOut } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
  }, [user, navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        Loading...
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        No profile data.
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4">
      <h1 className="text-3xl font-bold mb-6">Profile</h1>
      <Card>
        <CardHeader>
          <CardTitle>Profile Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center space-x-4 mb-4">
            <Avatar>
              <AvatarImage src={profile.avatar_url || `https://api.dicebear.com/7.x/lorelei/svg?seed=${profile.email}`} alt={profile.first_name} />
              <AvatarFallback>{profile.first_name?.[0]}{profile.last_name?.[0]}</AvatarFallback>
            </Avatar>
            <div>
              <h2 className="text-xl font-semibold">{profile.first_name} {profile.last_name}</h2>
              <p className="text-muted-foreground">{profile.email}</p>
              <Badge variant={profile.role === 'admin' ? "default" : "secondary"}>{profile.role}</Badge>
            </div>
          </div>
          <Tabs defaultValue="personal" className="w-full">
            <TabsList>
              <TabsTrigger value="personal">Personal Info</TabsTrigger>
              <TabsTrigger value="security">Security</TabsTrigger>
              <TabsTrigger value="gdpr">GDPR</TabsTrigger>
            </TabsList>
            <TabsContent value="personal" className="space-y-4 mt-4">
              <PersonalInfoForm profile={profile} onSubmit={(data) => updateProfile(profile.id, data)} />
            </TabsContent>
            <TabsContent value="security" className="space-y-4 mt-4">
              <MFASettings mfaEnabled={profile.mfa_enabled} onMFAChange={() => {}} />
            </TabsContent>
            <TabsContent value="gdpr" className="space-y-4 mt-4">
              <GDPRControls profile={profile} />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default Profile;